/**
 * Input — Carnaf design system v1.0
 * Spec: docs/strategy/car-as-place/design-system-2026-05-22/04-components.md § 4
 *
 * Label-above-input (no floating labels). 12px radius, 1px line, padding
 * 12/16. Focus state: 2px teal-soft ring + teal border. Error state: red
 * border + red helper text. Disabled: paper-sunk + ink-faint.
 *
 * For numeric / identifier inputs (plate, VIN, phone), pass `tabular` and
 * `dir="ltr"` so digits don't flip even when the surrounding page is RTL.
 */

import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  /** Optional helper text below the input. Becomes red if `error` is true. */
  helper?: ReactNode;
  error?: boolean;
  /** Render with tabular-nums and dir="ltr" — for plates, VINs, phones. */
  tabular?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, helper, error = false, tabular = false, className = '', dir, ...rest },
  ref,
) {
  const id = rest.id ?? rest.name;
  const resolvedDir = dir ?? (tabular ? 'ltr' : undefined);
  return (
    <div>
      {label && (
        <label htmlFor={id} className="block text-body-sm font-medium text-carnaf-ink-muted mb-1.5">
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={id}
        dir={resolvedDir}
        className={[
          'w-full px-4 py-3 rounded-md bg-white text-body',
          error
            ? 'border border-carnaf-red focus:outline-none focus:ring-2 focus:ring-carnaf-red-soft'
            : 'border border-carnaf-line focus:outline-none focus:ring-2 focus:ring-carnaf-teal-soft focus:border-carnaf-teal',
          'disabled:bg-carnaf-paper-sunk disabled:text-carnaf-ink-faint disabled:cursor-not-allowed',
          tabular ? 'tabular' : '',
          resolvedDir === 'ltr' ? 'text-end' : '',
          className,
        ].join(' ')}
        style={{ minHeight: 48 }}
        {...rest}
      />
      {helper && <p className={`mt-1 text-body-sm ${error ? 'text-carnaf-red' : 'text-carnaf-ink-muted'}`}>{helper}</p>}
    </div>
  );
});
